/**
 * ChannelSettingsPage - Dedicated full-page settings for a single channel
 * Replaces the dropdown/inline editing approach with a comprehensive settings page
 * 
 * Also handles Conference Network settings when the channel has is_conference_network: true
 */
import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  ArrowLeft, Save, Loader2, Radio, Youtube, List, Link2, 
  Image, Palette, Users, Tv, Settings, Copy, Check,
  Play, ExternalLink, Plus, Trash2, GripVertical, Globe, Menu
} from "lucide-react";
import { Button } from "../components/ui/button";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getAuthHeader = () => {
  const token = localStorage.getItem("auth_token");
  return { Authorization: `Bearer ${token}` };
};

export const ChannelSettingsPage = () => {
  const { channelId } = useParams();
  const navigate = useNavigate();
  const [channel, setChannel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeSection, setActiveSection] = useState("branding");
  const [copiedField, setCopiedField] = useState(null);
  const [syndicationSources, setSyndicationSources] = useState([]);
  
  // Member School Modal State
  const [showSchoolModal, setShowSchoolModal] = useState(false);
  const [editingSchoolIndex, setEditingSchoolIndex] = useState(null);
  const [schoolForm, setSchoolForm] = useState({ name: "", abbreviation: "", logo_url: "", athletic_website: "" });
  const [savingSchool, setSavingSchool] = useState(false);
  
  const [formData, setFormData] = useState({
    name: "",
    abbreviation: "",
    description: "",
    logo_url: "",
    thumbnail_url: "",
    banner_url: "",
    primary_color: "#f97316",
    secondary_color: "#000000",
    source_type: "rtmp",
    push_point_enabled: false,
    push_point_url: "",
    youtube_channel_id: "",
    fallback_video_url: "",
    is_24_7_enabled: false,
    playlist_shuffle: false,
    playlist_loop: true,
    live_override_priority: true,
    sponsor_enabled: false,
    sponsor_name: "",
    sponsor_logo: "",
    // Conference-specific fields
    is_conference_network: false,
    slug: "",
    member_schools: [],
    menu_items: [],
    syndication_source_ids: []
  });

  useEffect(() => {
    fetchChannel();
    fetchSyndicationSources();
  }, [channelId]);

  const fetchChannel = async () => {
    try {
      const res = await axios.get(`${API}/channels/${channelId}`);
      const ch = res.data;
      setChannel(ch);
      
      let sourceType = 'rtmp';
      if (ch.is_24_7_enabled) sourceType = '24_7_playlist';
      else if (ch.youtube_channel_id) sourceType = 'youtube_push';
      else if (ch.push_point_enabled) sourceType = 'push_point';
      
      setFormData({
        name: ch.name || "",
        abbreviation: ch.abbreviation || "",
        description: ch.description || "",
        logo_url: ch.logo_url || "",
        thumbnail_url: ch.thumbnail_url || "",
        banner_url: ch.banner_url || "",
        primary_color: ch.primary_color || "#f97316",
        secondary_color: ch.secondary_color || "#000000",
        source_type: sourceType,
        push_point_enabled: ch.push_point_enabled || false,
        push_point_url: ch.push_point_url || "",
        youtube_channel_id: ch.youtube_channel_id || "",
        fallback_video_url: ch.fallback_video_url || "",
        is_24_7_enabled: ch.is_24_7_enabled || false,
        playlist_shuffle: ch.playlist_shuffle || false,
        playlist_loop: ch.playlist_loop !== false,
        live_override_priority: ch.live_override_priority !== false,
        sponsor_enabled: ch.sponsor_enabled || false,
        sponsor_name: ch.sponsor_name || "",
        sponsor_logo: ch.sponsor_logo || "",
        // Conference-specific fields
        is_conference_network: ch.is_conference_network || false,
        slug: ch.slug || "",
        member_schools: ch.member_schools || [],
        menu_items: ch.menu_items || [],
        syndication_source_ids: ch.syndication_source_ids || []
      });
    } catch (err) {
      console.error("Error fetching channel:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchSyndicationSources = async () => {
    try {
      const res = await axios.get(`${API}/syndication/sources`, { headers: getAuthHeader() });
      setSyndicationSources(res.data);
    } catch (err) {
      console.error("Error fetching syndication sources:", err);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const saveData = {
        channel_id: channelId,
        name: formData.name,
        abbreviation: formData.abbreviation,
        description: formData.description,
        logo_url: formData.logo_url,
        thumbnail_url: formData.thumbnail_url,
        banner_url: formData.banner_url,
        primary_color: formData.primary_color,
        secondary_color: formData.secondary_color,
        push_point_enabled: formData.source_type === 'push_point',
        push_point_url: formData.push_point_url,
        youtube_channel_id: formData.source_type === 'youtube_push' ? formData.youtube_channel_id : '',
        fallback_video_url: formData.fallback_video_url,
        is_24_7_enabled: formData.source_type === '24_7_playlist',
        playlist_shuffle: formData.playlist_shuffle,
        playlist_loop: formData.playlist_loop,
        live_override_priority: formData.live_override_priority,
        sponsor_enabled: formData.sponsor_enabled,
        sponsor_name: formData.sponsor_name,
        sponsor_logo: formData.sponsor_logo
      };
      
      // Always save channel branding data first
      await axios.put(`${API}/channels/${channelId}/branding`, saveData, { headers: getAuthHeader() });
      
      // If this is a conference network, ALSO update conference-specific fields
      if (formData.is_conference_network) {
        await axios.put(`${API}/conference-networks/${channelId}`, {
          name: formData.name,
          slug: formData.slug,
          abbreviation: formData.abbreviation,
          logo_url: formData.logo_url,
          thumbnail_url: formData.thumbnail_url,
          banner_url: formData.banner_url,
          primary_color: formData.primary_color,
          secondary_color: formData.secondary_color,
          member_schools: formData.member_schools,
          menu_items: formData.menu_items,
          syndication_source_ids: formData.syndication_source_ids
        }, { headers: getAuthHeader() });
      }
      
      fetchChannel();
    } catch (err) {
      alert("Error saving: " + (err.response?.data?.detail || err.message));
    } finally {
      setSaving(false);
    }
  };

  // Conference-specific helper functions
  const openSchoolModal = (index = null) => {
    if (index !== null) {
      // Editing existing school
      setEditingSchoolIndex(index);
      setSchoolForm({ ...formData.member_schools[index] });
    } else {
      // Adding new school
      setEditingSchoolIndex(null);
      setSchoolForm({ name: "", abbreviation: "", logo_url: "", athletic_website: "" });
    }
    setShowSchoolModal(true);
  };

  const closeSchoolModal = () => {
    setShowSchoolModal(false);
    setEditingSchoolIndex(null);
    setSchoolForm({ name: "", abbreviation: "", logo_url: "", athletic_website: "" });
  };

  const saveSchool = async () => {
    if (!schoolForm.name.trim()) {
      alert("School name is required");
      return;
    }
    
    setSavingSchool(true);
    try {
      let updatedSchools;
      if (editingSchoolIndex !== null) {
        // Update existing school
        updatedSchools = [...formData.member_schools];
        updatedSchools[editingSchoolIndex] = { 
          ...updatedSchools[editingSchoolIndex],
          ...schoolForm 
        };
      } else {
        // Add new school
        updatedSchools = [
          ...formData.member_schools,
          { 
            id: `school-${Date.now()}`, 
            ...schoolForm,
            order: formData.member_schools.length 
          }
        ];
      }
      
      // Save immediately to backend
      await axios.put(`${API}/conference-networks/${channelId}`, {
        name: formData.name,
        slug: formData.slug,
        abbreviation: formData.abbreviation,
        logo_url: formData.logo_url,
        thumbnail_url: formData.thumbnail_url,
        banner_url: formData.banner_url,
        primary_color: formData.primary_color,
        secondary_color: formData.secondary_color,
        member_schools: updatedSchools,
        menu_items: formData.menu_items,
        syndication_source_ids: formData.syndication_source_ids
      }, { headers: getAuthHeader() });
      
      // Update local state
      setFormData({ ...formData, member_schools: updatedSchools });
      closeSchoolModal();
    } catch (err) {
      alert("Error saving school: " + (err.response?.data?.detail || err.message));
    } finally {
      setSavingSchool(false);
    }
  };

  const removeSchool = async (index) => {
    if (!confirm("Remove this school from the conference?")) return;
    
    setSaving(true);
    try {
      const updatedSchools = formData.member_schools.filter((_, i) => i !== index);
      
      await axios.put(`${API}/conference-networks/${channelId}`, {
        name: formData.name,
        slug: formData.slug,
        abbreviation: formData.abbreviation,
        logo_url: formData.logo_url,
        thumbnail_url: formData.thumbnail_url,
        banner_url: formData.banner_url,
        primary_color: formData.primary_color,
        secondary_color: formData.secondary_color,
        member_schools: updatedSchools,
        menu_items: formData.menu_items,
        syndication_source_ids: formData.syndication_source_ids
      }, { headers: getAuthHeader() });
      
      setFormData({ ...formData, member_schools: updatedSchools });
    } catch (err) {
      alert("Error removing school: " + (err.response?.data?.detail || err.message));
    } finally {
      setSaving(false);
    }
  };

  // Legacy functions for backward compatibility (used by other sections)
  const addMemberSchool = () => openSchoolModal();

  const updateMemberSchool = (index, field, value) => {
    const updated = [...formData.member_schools];
    updated[index] = { ...updated[index], [field]: value };
    setFormData({ ...formData, member_schools: updated });
  };

  const removeMemberSchool = (index) => removeSchool(index);

  const addMenuItem = () => {
    setFormData({
      ...formData,
      menu_items: [
        ...formData.menu_items,
        { id: `temp-${Date.now()}`, label: "", href: "", type: "internal", order: formData.menu_items.length }
      ]
    });
  };

  const updateMenuItem = (index, field, value) => {
    const updated = [...formData.menu_items];
    updated[index] = { ...updated[index], [field]: value };
    setFormData({ ...formData, menu_items: updated });
  };

  const removeMenuItem = (index) => {
    setFormData({
      ...formData,
      menu_items: formData.menu_items.filter((_, i) => i !== index)
    });
  };

  const toggleSyndicationSource = (sourceId) => {
    const current = formData.syndication_source_ids;
    if (current.includes(sourceId)) {
      setFormData({ ...formData, syndication_source_ids: current.filter(id => id !== sourceId) });
    } else {
      setFormData({ ...formData, syndication_source_ids: [...current, sourceId] });
    }
  };

  const copyToClipboard = (text, field) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const sections = [
    { id: "branding", name: "Branding", icon: Palette },
    { id: "source", name: "Video Source", icon: Radio },
    { id: "sponsor", name: "Sponsor", icon: Users },
    { id: "stream", name: "Stream Info", icon: Tv },
    // Conference-specific sections (conditionally rendered)
    ...(formData.is_conference_network ? [
      { id: "schools", name: "Member Schools", icon: Globe },
      { id: "menu", name: "Menu Items", icon: Menu },
      { id: "data", name: "Data Sources", icon: Link2 }
    ] : [])
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
      </div>
    );
  }

  if (!channel) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center text-white">
          <h1 className="text-2xl font-bold mb-2">Channel Not Found</h1>
          <Link to="/admin" className="text-orange-500 hover:underline">Back to Admin</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black">
      {/* Header */}
      <header className="bg-zinc-900 border-b border-white/10 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/admin" className="text-zinc-400 hover:text-white transition-colors">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div className="flex items-center gap-3">
              {channel.logo_url ? (
                <img src={channel.logo_url} alt="" className="w-10 h-10 object-contain" />
              ) : (
                <div className="w-10 h-10 bg-orange-500 rounded flex items-center justify-center text-white font-bold">
                  {channel.abbreviation || channel.name?.charAt(0)}
                </div>
              )}
              <div>
                <h1 className="text-white font-bold text-lg">{channel.name}</h1>
                <p className="text-zinc-500 text-xs">Channel Settings</p>
              </div>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            <a
              href={`/watch/${channelId}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-zinc-400 hover:text-white text-sm"
            >
              <ExternalLink className="w-4 h-4" />
              View Channel
            </a>
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-orange-500 hover:bg-orange-600 text-black"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
              Save Changes
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="flex gap-8">
          {/* Sidebar Navigation */}
          <div className="w-56 flex-shrink-0">
            <nav className="space-y-1">
              {sections.map((section) => {
                const Icon = section.icon;
                return (
                  <button
                    key={section.id}
                    onClick={() => setActiveSection(section.id)}
                    className={`w-full flex items-center gap-3 px-4 py-3 text-sm font-medium transition-colors ${
                      activeSection === section.id
                        ? "bg-orange-500/20 text-orange-500 border-l-2 border-orange-500"
                        : "text-zinc-400 hover:text-white hover:bg-white/5"
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    {section.name}
                  </button>
                );
              })}
            </nav>
            
            {/* Quick Actions */}
            <div className="mt-8 pt-8 border-t border-white/10">
              <h4 className="text-xs font-bold uppercase text-zinc-500 mb-4">Quick Actions</h4>
              <div className="space-y-2">
                <Link
                  to={`/admin/channel/${channelId}/playlist`}
                  className="flex items-center gap-2 text-sm text-zinc-400 hover:text-white"
                >
                  <List className="w-4 h-4" />
                  Manage Playlist
                </Link>
                {formData.is_conference_network && formData.slug && (
                  <a
                    href={`/network/${formData.slug}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-sm text-zinc-400 hover:text-white"
                  >
                    <Globe className="w-4 h-4" />
                    View Network Page
                  </a>
                )}
              </div>
            </div>
          </div>

          {/* Content Area */}
          <div className="flex-1 bg-zinc-900/50 border border-white/10 p-6">
            {/* Branding Section */}
            {activeSection === "branding" && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
                <div className="flex items-center justify-between">
                  <h2 className="text-xl font-bold text-white uppercase tracking-wider">Branding</h2>
                  {formData.is_conference_network && (
                    <span className="px-3 py-1 bg-purple-500/20 text-purple-400 text-xs font-bold uppercase rounded">
                      Conference Network
                    </span>
                  )}
                </div>
                
                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <label className="block text-xs font-bold uppercase text-zinc-500 mb-2">Channel Name</label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full bg-zinc-800 border border-white/10 text-white px-4 py-3"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold uppercase text-zinc-500 mb-2">Abbreviation</label>
                    <input
                      type="text"
                      value={formData.abbreviation}
                      onChange={(e) => setFormData({ ...formData, abbreviation: e.target.value })}
                      placeholder="e.g., ASN1"
                      className="w-full bg-zinc-800 border border-white/10 text-white px-4 py-3"
                    />
                  </div>
                </div>

                {/* Conference Network URL Slug */}
                {formData.is_conference_network && (
                  <div>
                    <label className="block text-xs font-bold uppercase text-zinc-500 mb-2">URL Slug</label>
                    <div className="flex items-center gap-2">
                      <span className="text-zinc-500">/network/</span>
                      <input
                        type="text"
                        value={formData.slug}
                        onChange={(e) => setFormData({ ...formData, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') })}
                        placeholder="conference-name"
                        className="flex-1 bg-zinc-800 border border-white/10 text-white px-4 py-3"
                      />
                    </div>
                    <p className="text-zinc-600 text-xs mt-1">
                      This determines the public URL of your conference network page
                    </p>
                  </div>
                )}
                
                <div>
                  <label className="block text-xs font-bold uppercase text-zinc-500 mb-2">Description</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows={3}
                    className="w-full bg-zinc-800 border border-white/10 text-white px-4 py-3"
                  />
                </div>

                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <label className="block text-xs font-bold uppercase text-zinc-500 mb-2">Primary Color</label>
                    <div className="flex items-center gap-3">
                      <input
                        type="color"
                        value={formData.primary_color}
                        onChange={(e) => setFormData({ ...formData, primary_color: e.target.value })}
                        className="w-12 h-12 bg-transparent border-0 cursor-pointer"
                      />
                      <input
                        type="text"
                        value={formData.primary_color}
                        onChange={(e) => setFormData({ ...formData, primary_color: e.target.value })}
                        className="flex-1 bg-zinc-800 border border-white/10 text-white px-4 py-3"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-bold uppercase text-zinc-500 mb-2">Secondary Color</label>
                    <div className="flex items-center gap-3">
                      <input
                        type="color"
                        value={formData.secondary_color}
                        onChange={(e) => setFormData({ ...formData, secondary_color: e.target.value })}
                        className="w-12 h-12 bg-transparent border-0 cursor-pointer"
                      />
                      <input
                        type="text"
                        value={formData.secondary_color}
                        onChange={(e) => setFormData({ ...formData, secondary_color: e.target.value })}
                        className="flex-1 bg-zinc-800 border border-white/10 text-white px-4 py-3"
                      />
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-6">
                  <div>
                    <label className="block text-xs font-bold uppercase text-zinc-500 mb-2">Logo URL</label>
                    <input
                      type="url"
                      value={formData.logo_url}
                      onChange={(e) => setFormData({ ...formData, logo_url: e.target.value })}
                      placeholder="https://..."
                      className="w-full bg-zinc-800 border border-white/10 text-white px-4 py-3"
                    />
                    {formData.logo_url && (
                      <img src={formData.logo_url} alt="Logo preview" className="mt-2 h-16 object-contain" />
                    )}
                  </div>
                  <div>
                    <label className="block text-xs font-bold uppercase text-zinc-500 mb-2">Thumbnail URL</label>
                    <input
                      type="url"
                      value={formData.thumbnail_url}
                      onChange={(e) => setFormData({ ...formData, thumbnail_url: e.target.value })}
                      placeholder="https://..."
                      className="w-full bg-zinc-800 border border-white/10 text-white px-4 py-3"
                    />
                    {formData.thumbnail_url && (
                      <img src={formData.thumbnail_url} alt="Thumbnail preview" className="mt-2 h-16 object-cover" />
                    )}
                  </div>
                  <div>
                    <label className="block text-xs font-bold uppercase text-zinc-500 mb-2">Banner URL</label>
                    <input
                      type="url"
                      value={formData.banner_url}
                      onChange={(e) => setFormData({ ...formData, banner_url: e.target.value })}
                      placeholder="https://..."
                      className="w-full bg-zinc-800 border border-white/10 text-white px-4 py-3"
                    />
                    {formData.banner_url && (
                      <img src={formData.banner_url} alt="Banner preview" className="mt-2 h-16 object-cover w-full" />
                    )}
                  </div>
                </div>
              </motion.div>
            )}

            {/* Video Source Section */}
            {activeSection === "source" && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
                <h2 className="text-xl font-bold text-white uppercase tracking-wider">Video Source</h2>
                
                <div className="grid grid-cols-2 gap-4">
                  {[
                    { id: "rtmp", name: "RTMP Stream", icon: Radio, desc: "Direct OBS/Encoder stream" },
                    { id: "push_point", name: "Push Point URL", icon: Link2, desc: "External stream URL" },
                    { id: "youtube_push", name: "YouTube Push", icon: Youtube, desc: "Embed YouTube stream" },
                    { id: "24_7_playlist", name: "24/7 Playlist", icon: List, desc: "Loop video playlist" }
                  ].map((source) => {
                    const Icon = source.icon;
                    return (
                      <button
                        key={source.id}
                        onClick={() => setFormData({ ...formData, source_type: source.id })}
                        className={`p-4 border text-left transition-all ${
                          formData.source_type === source.id
                            ? "border-orange-500 bg-orange-500/10"
                            : "border-white/10 hover:border-white/20"
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <Icon className={`w-5 h-5 ${formData.source_type === source.id ? "text-orange-500" : "text-zinc-400"}`} />
                          <div>
                            <p className="text-white font-medium">{source.name}</p>
                            <p className="text-zinc-500 text-xs">{source.desc}</p>
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>

                {/* Source-specific settings */}
                {formData.source_type === "push_point" && (
                  <div className="mt-6">
                    <label className="block text-xs font-bold uppercase text-zinc-500 mb-2">Push Point URL</label>
                    <input
                      type="url"
                      value={formData.push_point_url}
                      onChange={(e) => setFormData({ ...formData, push_point_url: e.target.value })}
                      placeholder="https://stream.example.com/live.m3u8"
                      className="w-full bg-zinc-800 border border-white/10 text-white px-4 py-3"
                    />
                  </div>
                )}

                {formData.source_type === "youtube_push" && (
                  <div className="mt-6">
                    <label className="block text-xs font-bold uppercase text-zinc-500 mb-2">YouTube Channel ID</label>
                    <input
                      type="text"
                      value={formData.youtube_channel_id}
                      onChange={(e) => setFormData({ ...formData, youtube_channel_id: e.target.value })}
                      placeholder="UCxxxxxxxxxxxxxxxx"
                      className="w-full bg-zinc-800 border border-white/10 text-white px-4 py-3"
                    />
                    <p className="text-zinc-500 text-xs mt-2">
                      Find your channel ID in YouTube Studio → Settings → Channel → Basic info
                    </p>
                  </div>
                )}

                {formData.source_type === "24_7_playlist" && (
                  <div className="mt-6 space-y-4">
                    <div className="flex items-center gap-4">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData.playlist_shuffle}
                          onChange={(e) => setFormData({ ...formData, playlist_shuffle: e.target.checked })}
                          className="w-4 h-4 accent-orange-500"
                        />
                        <span className="text-white text-sm">Shuffle playlist</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData.playlist_loop}
                          onChange={(e) => setFormData({ ...formData, playlist_loop: e.target.checked })}
                          className="w-4 h-4 accent-orange-500"
                        />
                        <span className="text-white text-sm">Loop playlist</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData.live_override_priority}
                          onChange={(e) => setFormData({ ...formData, live_override_priority: e.target.checked })}
                          className="w-4 h-4 accent-orange-500"
                        />
                        <span className="text-white text-sm">Live games override playlist</span>
                      </label>
                    </div>
                    <Link
                      to={`/admin/channel/${channelId}/playlist`}
                      className="inline-flex items-center gap-2 text-orange-500 hover:text-orange-400"
                    >
                      <List className="w-4 h-4" />
                      Manage Playlist Items
                    </Link>
                  </div>
                )}

                <div className="mt-6">
                  <label className="block text-xs font-bold uppercase text-zinc-500 mb-2">Fallback Video URL</label>
                  <input
                    type="url"
                    value={formData.fallback_video_url}
                    onChange={(e) => setFormData({ ...formData, fallback_video_url: e.target.value })}
                    placeholder="Video to show when no live stream"
                    className="w-full bg-zinc-800 border border-white/10 text-white px-4 py-3"
                  />
                </div>
              </motion.div>
            )}

            {/* Sponsor Section */}
            {activeSection === "sponsor" && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
                <h2 className="text-xl font-bold text-white uppercase tracking-wider">Sponsor</h2>
                
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.sponsor_enabled}
                    onChange={(e) => setFormData({ ...formData, sponsor_enabled: e.target.checked })}
                    className="w-5 h-5 accent-orange-500"
                  />
                  <span className="text-white">Enable sponsor display</span>
                </label>

                {formData.sponsor_enabled && (
                  <div className="grid grid-cols-2 gap-6">
                    <div>
                      <label className="block text-xs font-bold uppercase text-zinc-500 mb-2">Sponsor Name</label>
                      <input
                        type="text"
                        value={formData.sponsor_name}
                        onChange={(e) => setFormData({ ...formData, sponsor_name: e.target.value })}
                        placeholder="Sponsor company name"
                        className="w-full bg-zinc-800 border border-white/10 text-white px-4 py-3"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-bold uppercase text-zinc-500 mb-2">Sponsor Logo URL</label>
                      <input
                        type="url"
                        value={formData.sponsor_logo}
                        onChange={(e) => setFormData({ ...formData, sponsor_logo: e.target.value })}
                        placeholder="https://..."
                        className="w-full bg-zinc-800 border border-white/10 text-white px-4 py-3"
                      />
                      {formData.sponsor_logo && (
                        <img src={formData.sponsor_logo} alt="Sponsor preview" className="mt-2 h-12 object-contain" />
                      )}
                    </div>
                  </div>
                )}
              </motion.div>
            )}

            {/* Stream Info Section */}
            {activeSection === "stream" && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
                <h2 className="text-xl font-bold text-white uppercase tracking-wider">Stream Information</h2>
                
                {channel.rtmp_url && (
                  <div className="bg-zinc-800 p-4 space-y-4">
                    <div>
                      <label className="block text-xs font-bold uppercase text-zinc-500 mb-2">RTMP Server URL</label>
                      <div className="flex items-center gap-2">
                        <code className="flex-1 bg-zinc-900 text-orange-400 px-4 py-2 text-sm font-mono">
                          {channel.rtmp_url}
                        </code>
                        <button
                          onClick={() => copyToClipboard(channel.rtmp_url, "rtmp")}
                          className="p-2 text-zinc-400 hover:text-white"
                        >
                          {copiedField === "rtmp" ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>
                    
                    <div>
                      <label className="block text-xs font-bold uppercase text-zinc-500 mb-2">Stream Key</label>
                      <div className="flex items-center gap-2">
                        <code className="flex-1 bg-zinc-900 text-orange-400 px-4 py-2 text-sm font-mono">
                          {channel.stream_key}
                        </code>
                        <button
                          onClick={() => copyToClipboard(channel.stream_key, "key")}
                          className="p-2 text-zinc-400 hover:text-white"
                        >
                          {copiedField === "key" ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>

                    <p className="text-zinc-500 text-xs">
                      Use these credentials in OBS or your streaming software to go live on this channel.
                    </p>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div className="bg-zinc-800 p-4">
                    <p className="text-zinc-500 mb-1">Channel ID</p>
                    <p className="text-white font-mono">{channel.channel_id}</p>
                  </div>
                  <div className="bg-zinc-800 p-4">
                    <p className="text-zinc-500 mb-1">Status</p>
                    <p className={`font-bold ${channel.is_live ? "text-red-500" : "text-zinc-400"}`}>
                      {channel.is_live ? "LIVE" : "Offline"}
                    </p>
                  </div>
                </div>

                {/* Conference Network Badge & Link */}
                {formData.is_conference_network && formData.slug && (
                  <div className="mt-6 p-4 bg-purple-500/10 border border-purple-500/30">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Globe className="w-5 h-5 text-purple-400" />
                        <span className="text-purple-400 font-bold uppercase text-sm">Conference Network</span>
                      </div>
                      <a
                        href={`/network/${formData.slug}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-1 text-sm text-purple-400 hover:text-purple-300"
                      >
                        <ExternalLink className="w-4 h-4" />
                        View Network Page
                      </a>
                    </div>
                    <p className="text-zinc-400 text-xs mt-2">
                      URL: /network/{formData.slug}
                    </p>
                  </div>
                )}
              </motion.div>
            )}

            {/* Conference: Member Schools Section */}
            {activeSection === "schools" && formData.is_conference_network && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
                <div className="flex items-center justify-between">
                  <h2 className="text-xl font-bold text-white uppercase tracking-wider">Member Schools</h2>
                  <Button onClick={() => openSchoolModal()} className="bg-orange-500 hover:bg-orange-600 text-black" data-testid="add-school-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    Add School
                  </Button>
                </div>
                
                <p className="text-zinc-400 text-sm">
                  Add member schools. Their logos will appear at the top of the network page, linked to their athletic websites.
                  <span className="text-green-400 ml-2">✓ Changes save automatically</span>
                </p>

                {formData.member_schools.length === 0 ? (
                  <div className="bg-zinc-800 p-8 text-center border border-dashed border-white/20">
                    <Users className="w-12 h-12 text-zinc-600 mx-auto mb-3" />
                    <p className="text-zinc-400">No member schools added yet</p>
                    <Button onClick={() => openSchoolModal()} className="mt-4 bg-zinc-700 hover:bg-zinc-600 text-white">
                      <Plus className="w-4 h-4 mr-2" /> Add Your First School
                    </Button>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {formData.member_schools.map((school, index) => (
                      <div 
                        key={school.id || index} 
                        className="bg-zinc-800/50 p-4 border border-white/10 hover:border-white/20 transition-colors group"
                        data-testid={`school-card-${index}`}
                      >
                        <div className="flex items-center gap-4">
                          {/* School Logo */}
                          <div className="w-16 h-16 bg-zinc-900 rounded flex items-center justify-center flex-shrink-0 overflow-hidden">
                            {school.logo_url ? (
                              <img src={school.logo_url} alt={school.name} className="w-full h-full object-contain" />
                            ) : (
                              <span className="text-2xl font-bold text-zinc-500">
                                {school.abbreviation?.charAt(0) || school.name?.charAt(0) || "?"}
                              </span>
                            )}
                          </div>
                          
                          {/* School Info */}
                          <div className="flex-1 min-w-0">
                            <h3 className="text-white font-bold truncate">{school.name || "Unnamed School"}</h3>
                            <p className="text-zinc-400 text-sm">{school.abbreviation || "—"}</p>
                            {school.athletic_website && (
                              <a 
                                href={school.athletic_website} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="text-xs text-orange-400 hover:text-orange-300 truncate block"
                              >
                                {school.athletic_website.replace(/^https?:\/\//, '')}
                              </a>
                            )}
                          </div>
                          
                          {/* Actions */}
                          <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                            <Button
                              onClick={() => openSchoolModal(index)}
                              className="bg-zinc-700 hover:bg-zinc-600 text-white"
                              size="sm"
                              data-testid={`edit-school-${index}`}
                            >
                              Edit
                            </Button>
                            <Button
                              onClick={() => removeSchool(index)}
                              className="bg-red-500/20 hover:bg-red-500/30 text-red-400"
                              size="sm"
                              data-testid={`remove-school-${index}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Add/Edit School Modal */}
                {showSchoolModal && (
                  <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50" onClick={closeSchoolModal}>
                    <motion.div 
                      initial={{ opacity: 0, scale: 0.95 }}
                      animate={{ opacity: 1, scale: 1 }}
                      className="bg-zinc-900 border border-white/10 p-6 w-full max-w-lg mx-4"
                      onClick={(e) => e.stopPropagation()}
                      data-testid="school-modal"
                    >
                      <h3 className="text-xl font-bold text-white uppercase tracking-wider mb-6">
                        {editingSchoolIndex !== null ? "Edit School" : "Add Member School"}
                      </h3>
                      
                      <div className="space-y-4">
                        <div>
                          <label className="block text-xs font-bold uppercase text-zinc-500 mb-2">School Name *</label>
                          <input
                            type="text"
                            value={schoolForm.name}
                            onChange={(e) => setSchoolForm({ ...schoolForm, name: e.target.value })}
                            placeholder="e.g., Louisiana State University Shreveport"
                            className="w-full bg-zinc-800 border border-white/10 text-white px-4 py-3 focus:border-orange-500 focus:outline-none"
                            autoFocus
                            data-testid="school-name-input"
                          />
                        </div>
                        
                        <div>
                          <label className="block text-xs font-bold uppercase text-zinc-500 mb-2">Abbreviation</label>
                          <input
                            type="text"
                            value={schoolForm.abbreviation}
                            onChange={(e) => setSchoolForm({ ...schoolForm, abbreviation: e.target.value.toUpperCase() })}
                            placeholder="e.g., LSUS"
                            maxLength={10}
                            className="w-full bg-zinc-800 border border-white/10 text-white px-4 py-3 focus:border-orange-500 focus:outline-none"
                            data-testid="school-abbr-input"
                          />
                          <p className="text-zinc-600 text-xs mt-1">Shown in the header bar of the network page</p>
                        </div>
                        
                        <div>
                          <label className="block text-xs font-bold uppercase text-zinc-500 mb-2">Logo URL</label>
                          <input
                            type="url"
                            value={schoolForm.logo_url}
                            onChange={(e) => setSchoolForm({ ...schoolForm, logo_url: e.target.value })}
                            placeholder="https://example.com/logo.png"
                            className="w-full bg-zinc-800 border border-white/10 text-white px-4 py-3 focus:border-orange-500 focus:outline-none"
                            data-testid="school-logo-input"
                          />
                          {schoolForm.logo_url && (
                            <div className="mt-2 p-2 bg-zinc-800 inline-block">
                              <img src={schoolForm.logo_url} alt="Preview" className="h-12 w-auto object-contain" onError={(e) => e.target.style.display = 'none'} />
                            </div>
                          )}
                        </div>
                        
                        <div>
                          <label className="block text-xs font-bold uppercase text-zinc-500 mb-2">Athletic Website</label>
                          <input
                            type="url"
                            value={schoolForm.athletic_website}
                            onChange={(e) => setSchoolForm({ ...schoolForm, athletic_website: e.target.value })}
                            placeholder="https://athletics.example.edu"
                            className="w-full bg-zinc-800 border border-white/10 text-white px-4 py-3 focus:border-orange-500 focus:outline-none"
                            data-testid="school-website-input"
                          />
                          <p className="text-zinc-600 text-xs mt-1">Clicking the logo links to this website</p>
                        </div>
                      </div>
                      
                      <div className="flex items-center justify-end gap-3 mt-8">
                        <Button onClick={closeSchoolModal} className="bg-zinc-700 hover:bg-zinc-600 text-white" disabled={savingSchool}>
                          Cancel
                        </Button>
                        <Button 
                          onClick={saveSchool} 
                          className="bg-orange-500 hover:bg-orange-600 text-black"
                          disabled={savingSchool || !schoolForm.name.trim()}
                          data-testid="save-school-btn"
                        >
                          {savingSchool ? (
                            <>
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              Saving...
                            </>
                          ) : (
                            <>
                              <Save className="w-4 h-4 mr-2" />
                              {editingSchoolIndex !== null ? "Update School" : "Add School"}
                            </>
                          )}
                        </Button>
                      </div>
                    </motion.div>
                  </div>
                )}
              </motion.div>
            )}

            {/* Conference: Menu Items Section */}
            {activeSection === "menu" && formData.is_conference_network && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
                <div className="flex items-center justify-between">
                  <h2 className="text-xl font-bold text-white uppercase tracking-wider">Menu Items</h2>
                  <Button onClick={addMenuItem} className="bg-orange-500 hover:bg-orange-600 text-black">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Menu Item
                  </Button>
                </div>
                
                <p className="text-zinc-400 text-sm">
                  Configure the navigation menu. Default items (Schedule, Standings, Scores) are added automatically.
                </p>

                {formData.menu_items.length === 0 ? (
                  <div className="bg-zinc-800 p-8 text-center border border-dashed border-white/20">
                    <Menu className="w-12 h-12 text-zinc-600 mx-auto mb-3" />
                    <p className="text-zinc-400">No custom menu items</p>
                    <p className="text-zinc-500 text-xs mt-1">Default menu (Schedule, Standings, Scores) will be shown</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {formData.menu_items.map((item, index) => (
                      <div key={item.id} className="bg-zinc-800/50 p-4 border border-white/10 flex items-center gap-4">
                        <div className="flex-1 grid grid-cols-3 gap-4">
                          <div>
                            <label className="block text-[10px] text-zinc-500 uppercase mb-1">Label</label>
                            <input
                              type="text"
                              value={item.label}
                              onChange={(e) => updateMenuItem(index, "label", e.target.value)}
                              placeholder="Menu Label"
                              className="w-full bg-zinc-900 border border-white/10 text-white px-3 py-2 text-sm"
                            />
                          </div>
                          <div>
                            <label className="block text-[10px] text-zinc-500 uppercase mb-1">URL/Path</label>
                            <input
                              type="text"
                              value={item.href}
                              onChange={(e) => updateMenuItem(index, "href", e.target.value)}
                              placeholder="/path or https://..."
                              className="w-full bg-zinc-900 border border-white/10 text-white px-3 py-2 text-sm"
                            />
                          </div>
                          <div>
                            <label className="block text-[10px] text-zinc-500 uppercase mb-1">Type</label>
                            <select
                              value={item.type}
                              onChange={(e) => updateMenuItem(index, "type", e.target.value)}
                              className="w-full bg-zinc-900 border border-white/10 text-white px-3 py-2 text-sm"
                            >
                              <option value="internal">Internal Link</option>
                              <option value="external">External Link</option>
                            </select>
                          </div>
                        </div>
                        <Button
                          onClick={() => removeMenuItem(index)}
                          className="bg-red-500/20 hover:bg-red-500/30 text-red-400 flex-shrink-0"
                          size="sm"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </motion.div>
            )}

            {/* Conference: Data Sources Section */}
            {activeSection === "data" && formData.is_conference_network && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
                <h2 className="text-xl font-bold text-white uppercase tracking-wider">Data Sources</h2>
                
                <p className="text-zinc-400 text-sm">
                  Link syndication sources to this conference. Games from these sources will appear in the conference schedule.
                </p>

                {syndicationSources.length === 0 ? (
                  <div className="bg-zinc-800 p-8 text-center border border-dashed border-white/20">
                    <Link2 className="w-12 h-12 text-zinc-600 mx-auto mb-3" />
                    <p className="text-zinc-400">No syndication sources available</p>
                    <p className="text-zinc-500 text-xs mt-1">Add sources in the Syndication tab first</p>
                    <Link 
                      to="/admin" 
                      className="inline-block mt-4 text-sm text-orange-500 hover:text-orange-400"
                    >
                      Go to Admin → Syndication
                    </Link>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {syndicationSources.map((source) => (
                      <label
                        key={source.id}
                        className={`flex items-center gap-3 p-4 cursor-pointer transition-colors border ${
                          formData.syndication_source_ids.includes(source.id)
                            ? "bg-orange-500/10 border-orange-500/50"
                            : "bg-zinc-800/50 border-white/10 hover:bg-zinc-800"
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={formData.syndication_source_ids.includes(source.id)}
                          onChange={() => toggleSyndicationSource(source.id)}
                          className="w-5 h-5 accent-orange-500"
                        />
                        <div className="flex-1">
                          <p className="text-white font-medium">{source.name}</p>
                          <p className="text-zinc-500 text-xs truncate">{source.hudl_fan_url}</p>
                        </div>
                        {formData.syndication_source_ids.includes(source.id) && (
                          <Check className="w-5 h-5 text-orange-500" />
                        )}
                      </label>
                    ))}
                  </div>
                )}
              </motion.div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChannelSettingsPage;
